import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ShieldCheck,
  Lock,
  CreditCard,
  Mail,
  Phone,
  User as UserIcon,
  MapPin,
  CheckCircle2,
  Loader2,
  PartyPopper,
  ChevronRight,
} from "lucide-react";
import khayatGoLogo from "../assets/images/logo.png";
import { getApiUrl, getAuthKey } from "../utils/apiUtils";
import { useCountryCodes } from "../utils/useCountryCodes";
import { PRICING_PLANS, annualSavings } from "../utils/pricingPlans";
import BillingCycleToggle from "../components/BillingCycleToggle";

const luhnCheck = (digits) => {
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return digits.length >= 12 && sum % 10 === 0;
};

const formatCardNumber = (value) =>
  value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length < 3) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-khamco-ribbon focus:ring-2 focus:ring-khamco-ribbon/20 outline-none transition-all bg-white disabled:bg-gray-50 disabled:text-gray-500 text-black";
const labelClasses = "block text-sm font-semibold text-gray-700 mb-1.5";

function SectionCard({ icon, title, children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8"
    >
      <div className="flex items-center mb-6">
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mr-3 shrink-0">
          {icon}
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
      {children}
    </motion.div>
  );
}

function PlanPicker({ billingCycle, onBillingCycleChange, onSelect }) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="plan-picker"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.4 }}
      className="max-w-3xl mx-auto text-center"
    >
      <h2 className="text-2xl sm:text-3xl font-extrabold text-khamco-ribbon mb-3">
        {t("Choose your plan")}
      </h2>
      <p className="text-gray-600 mb-6">
        {t("Select a plan below to continue to checkout.")}
      </p>
      <div className="flex justify-center mb-10">
        <BillingCycleToggle value={billingCycle} onChange={onBillingCycleChange} />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        {Object.values(PRICING_PLANS).map((plan) => (
          <motion.button
            key={plan.id}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelect(plan.id)}
            className={`text-left rounded-3xl p-8 border shadow-sm hover:shadow-xl transition-shadow relative overflow-hidden ${
              plan.highlight
                ? "bg-khamco-ribbon border-khamco-ribbon text-white"
                : "bg-white border-gray-100 text-gray-900"
            }`}
          >
            {plan.highlight && (
              <div className="absolute top-0 right-0 bg-khamco-primary text-white text-xs font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
                {t("Most Popular")}
              </div>
            )}
            <h4 className="text-xl font-bold mb-1">{plan.name}</h4>
            <p className={`text-sm mb-6 ${plan.highlight ? "text-blue-200" : "text-gray-500"}`}>
              {plan.tagline}
            </p>
            <div className="mb-6">
              <span className="text-4xl font-extrabold">${plan.pricing[billingCycle]}</span>
              <span className={plan.highlight ? "text-blue-200" : "text-gray-500"}>
                /{billingCycle}
              </span>
              {billingCycle === "yr" && (
                <p className={`text-sm mt-1 ${plan.highlight ? "text-blue-200" : "text-gray-500"}`}>
                  {t("Save")} ${annualSavings(plan)}{t("/year vs. monthly")}
                </p>
              )}
            </div>
            <div
              className={`inline-flex items-center font-semibold ${
                plan.highlight ? "text-white" : "text-khamco-ribbon"
              }`}
            >
              {t("Select plan")} <ChevronRight className="w-4 h-4 ml-1" />
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}

export default function CheckoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiUrl = getApiUrl();
  const authKey = getAuthKey();
  const isAuthenticated = !!authKey;
  const { countryCodes } = useCountryCodes();

  const [searchParams, setSearchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const billingParam = searchParams.get("billing");
  const [selectedPlanId, setSelectedPlanId] = useState(
    planParam && PRICING_PLANS[planParam] ? planParam : null
  );
  const [billingCycle, setBillingCycle] = useState(billingParam === "yr" ? "yr" : "mo");
  const plan = selectedPlanId ? PRICING_PLANS[selectedPlanId] : null;

  const [contact, setContact] = useState({ name: "", surname: "", email: "", mobile: "" });
  const [mobileCountry, setMobileCountry] = useState({ name: "Kuwait", callingCode: "+965" });
  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });
  const [billing, setBilling] = useState({
    cardholderName: "",
    cardNumber: "",
    expiry: "",
    cvc: "",
  });

  useEffect(() => {
    document.title = "Checkout - KhayatGO";
  }, []);

  useEffect(() => {
    if (planParam && PRICING_PLANS[planParam] && planParam !== selectedPlanId) {
      setSelectedPlanId(planParam);
    }
  }, [planParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectPlan = (id) => {
    setSelectedPlanId(id);
    setSearchParams({ plan: id, billing: billingCycle }, { replace: true });
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    if (selectedPlanId) {
      setSearchParams({ plan: selectedPlanId, billing: cycle }, { replace: true });
    }
  };

  const userDetailsQuery = useQuery({
    queryKey: ["checkout-user-details"],
    queryFn: async () => {
      const response = await axios.get(`${apiUrl}/checkout/user-details`, {
        headers: { Authorization: `Bearer ${authKey}` },
      });
      return response.data.user;
    },
    enabled: isAuthenticated,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (userDetailsQuery.data) {
      setContact({
        name: userDetailsQuery.data.name || "",
        surname: userDetailsQuery.data.surname || "",
        email: userDetailsQuery.data.email || "",
        mobile: userDetailsQuery.data.mobile || "",
      });
      toast.info(
        `${t("Welcome back,")} ${userDetailsQuery.data.name}${t("! We've pre-filled your details.")}`,
        {
          toastId: "checkout-prefill",
        }
      );
    }
  }, [userDetailsQuery.data, t]);

  useEffect(() => {
    if (userDetailsQuery.isError) {
      toast.warn(t("Couldn't load your saved details. Please fill them in manually."), {
        toastId: "checkout-prefill-error",
      });
    }
  }, [userDetailsQuery.isError, t]);

  const checkoutMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await axios.post(`${apiUrl}/checkout`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || t("Payment successful!"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("Payment failed. Please check your details and try again.")
      );
    },
  });

  const total = useMemo(
    () => (plan ? plan.pricing[billingCycle] : 0),
    [plan, billingCycle]
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!plan) {
      toast.warn(t("Please select a plan to continue"));
      return;
    }
    if (!contact.name.trim() || !contact.surname.trim()) {
      toast.warn(t("Please enter your full name"));
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) {
      toast.warn(t("Please enter a valid email address"));
      return;
    }
    if (!contact.mobile.trim()) {
      toast.warn(t("Please enter your phone number"));
      return;
    }
    if (!address.line1.trim() || !address.city.trim() || !address.postalCode.trim() || !address.country.trim()) {
      toast.warn(t("Please complete your billing address"));
      return;
    }
    if (!billing.cardholderName.trim()) {
      toast.warn(t("Please enter the name on your card"));
      return;
    }
    const cardDigits = billing.cardNumber.replace(/\s/g, "");
    if (!luhnCheck(cardDigits)) {
      toast.warn(t("Please enter a valid card number"));
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(billing.expiry)) {
      toast.warn(t("Please enter a valid expiry date (MM/YY)"));
      return;
    }
    if (!/^\d{3,4}$/.test(billing.cvc)) {
      toast.warn(t("Please enter a valid CVC"));
      return;
    }

    checkoutMutation.mutate({
      planId: plan.id,
      billingCycle,
      customer: {
        name: contact.name.trim(),
        surname: contact.surname.trim(),
        email: contact.email.trim(),
        mobile: isAuthenticated ? contact.mobile : `${mobileCountry.callingCode}${contact.mobile.trim()}`,
      },
      address,
      billing: {
        cardholderName: billing.cardholderName.trim(),
        cardNumber: cardDigits,
        expiry: billing.expiry,
        cvc: billing.cvc,
      },
    });
  };

  const receipt = checkoutMutation.data?.receipt;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 py-4 px-4 sm:px-6 lg:px-8 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-gray-500 hover:text-khamco-ribbon transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            {t("Back")}
          </button>
          <img
            src={khayatGoLogo}
            alt="KhayatGO"
            className="w-auto h-8 object-contain"
          />
          <div className="hidden sm:flex items-center text-sm text-gray-400 font-medium">
            <Lock className="w-4 h-4 mr-1.5 text-green-600" />
            {t("Secure Checkout")}
          </div>
          <div className="sm:hidden w-10" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <AnimatePresence mode="wait">
          {receipt ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="max-w-lg mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.15 }}
                className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6"
              >
                <PartyPopper className="w-10 h-10 text-green-600" />
              </motion.div>
              <h2 className="text-2xl font-extrabold text-khamco-ribbon mb-2">
                {t("Payment Successful!")}
              </h2>
              <p className="text-gray-600 mb-8">
                {t("Thanks")} {receipt.customer.name}{t(", your")} {receipt.plan.name}{" "}
                {t("plan is ready to go. A confirmation has been sent to")} {receipt.customer.email}.
              </p>
              <div className="bg-gray-50 rounded-2xl p-6 text-left space-y-3 mb-8">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("Plan")}</span>
                  <span className="font-semibold text-gray-900">{receipt.plan.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("Amount charged")}</span>
                  <span className="font-semibold text-gray-900">
                    ${receipt.plan.price}/{receipt.plan.billingCycle}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("Card")}</span>
                  <span className="font-semibold text-gray-900">
                    {receipt.card.brand} •••• {receipt.card.last4}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">{t("Transaction ID")}</span>
                  <span className="font-mono text-xs text-gray-700">{receipt.transactionId}</span>
                </div>
              </div>
              <button
                onClick={() =>
                  navigate(isAuthenticated ? "/home" : "/users/login")
                }
                className="w-full bg-khamco-primary hover:bg-pink-600 text-white py-3.5 rounded-xl font-bold transition-colors shadow-lg"
              >
                {isAuthenticated ? t("Go to Dashboard") : t("Create Your Account")}
              </button>
            </motion.div>
          ) : !plan ? (
            <PlanPicker
              billingCycle={billingCycle}
              onBillingCycleChange={handleBillingCycleChange}
              onSelect={handleSelectPlan}
            />
          ) : (
            <motion.form
              key="checkout-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid lg:grid-cols-[1fr_380px] gap-8 items-start"
            >
              <div className="space-y-6 order-2 lg:order-1">
                {isAuthenticated ? (
                  <SectionCard
                    icon={<UserIcon className="w-5 h-5 text-khamco-ribbon" />}
                    title={t("Your Details")}
                  >
                    {userDetailsQuery.isLoading ? (
                      <div className="flex items-center text-gray-500">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("Loading your account details...")}
                      </div>
                    ) : (
                      <div className="flex items-center bg-blue-50 rounded-xl p-4">
                        <CheckCircle2 className="w-5 h-5 text-khamco-ribbon mr-3 shrink-0" />
                        <div className="text-sm">
                          <p className="font-semibold text-gray-900">
                            {contact.name} {contact.surname}
                          </p>
                          <p className="text-gray-500">{contact.email}</p>
                          {contact.mobile && <p className="text-gray-500">{contact.mobile}</p>}
                        </div>
                      </div>
                    )}
                  </SectionCard>
                ) : (
                  <SectionCard
                    icon={<UserIcon className="w-5 h-5 text-khamco-ribbon" />}
                    title={t("Contact Information")}
                  >
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClasses}>{t("First Name")}</label>
                        <input
                          className={inputClasses}
                          value={contact.name}
                          onChange={(e) => setContact({ ...contact, name: e.target.value })}
                          placeholder={t("Ahmad")}
                        />
                      </div>
                      <div>
                        <label className={labelClasses}>{t("Last Name")}</label>
                        <input
                          className={inputClasses}
                          value={contact.surname}
                          onChange={(e) => setContact({ ...contact, surname: e.target.value })}
                          placeholder={t("Al-Sabah")}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClasses}>
                          <Mail className="w-4 h-4 inline mr-1 -mt-0.5" /> {t("Email")}
                        </label>
                        <input
                          type="email"
                          className={inputClasses}
                          value={contact.email}
                          onChange={(e) => setContact({ ...contact, email: e.target.value })}
                          placeholder={t("you@example.com")}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClasses}>
                          <Phone className="w-4 h-4 inline mr-1 -mt-0.5" /> {t("Phone Number")}
                        </label>
                        <div className="flex gap-2">
                          <select
                            className={`${inputClasses} w-32 shrink-0`}
                            value={mobileCountry.name}
                            onChange={(e) => {
                              const found = countryCodes.find((c) => c.name === e.target.value);
                              if (found) setMobileCountry(found);
                            }}
                          >
                            {countryCodes.map((c) => (
                              <option key={`${c.name}-${c.callingCode}`} value={c.name}>
                                {c.callingCode} {c.name}
                              </option>
                            ))}
                          </select>
                          <input
                            className={inputClasses}
                            value={contact.mobile}
                            onChange={(e) => setContact({ ...contact, mobile: e.target.value })}
                            placeholder={t("12345678")}
                          />
                        </div>
                      </div>
                    </div>
                  </SectionCard>
                )}

                <SectionCard
                  icon={<MapPin className="w-5 h-5 text-khamco-ribbon" />}
                  title={t("Billing Address")}
                  delay={0.05}
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className={labelClasses}>{t("Address Line 1")}</label>
                      <input
                        className={inputClasses}
                        value={address.line1}
                        onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                        placeholder={t("Street address")}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClasses}>{t("Address Line 2 (optional)")}</label>
                      <input
                        className={inputClasses}
                        value={address.line2}
                        onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                        placeholder={t("Apartment, suite, etc.")}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t("City")}</label>
                      <input
                        className={inputClasses}
                        value={address.city}
                        onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t("State / Region")}</label>
                      <input
                        className={inputClasses}
                        value={address.state}
                        onChange={(e) => setAddress({ ...address, state: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t("Postal Code")}</label>
                      <input
                        className={inputClasses}
                        value={address.postalCode}
                        onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t("Country")}</label>
                      <select
                        className={inputClasses}
                        value={address.country}
                        onChange={(e) => setAddress({ ...address, country: e.target.value })}
                      >
                        <option value="">{t("Select country")}</option>
                        {countryCodes.map((c) => (
                          <option key={c.name} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </SectionCard>

                <SectionCard
                  icon={<CreditCard className="w-5 h-5 text-khamco-ribbon" />}
                  title={t("Payment Details")}
                  delay={0.1}
                >
                  <div className="mb-4 inline-flex items-center text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-full">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                    {t("Sandbox mode — no real charges will be made")}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className={labelClasses}>{t("Name on Card")}</label>
                      <input
                        className={inputClasses}
                        value={billing.cardholderName}
                        onChange={(e) => setBilling({ ...billing, cardholderName: e.target.value })}
                        placeholder={t("Name as shown on card")}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClasses}>{t("Card Number")}</label>
                      <input
                        className={inputClasses}
                        value={billing.cardNumber}
                        onChange={(e) =>
                          setBilling({ ...billing, cardNumber: formatCardNumber(e.target.value) })
                        }
                        placeholder={t("1234 5678 9012 3456")}
                        inputMode="numeric"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t("Expiry (MM/YY)")}</label>
                      <input
                        className={inputClasses}
                        value={billing.expiry}
                        onChange={(e) =>
                          setBilling({ ...billing, expiry: formatExpiry(e.target.value) })
                        }
                        placeholder={t("MM/YY")}
                        inputMode="numeric"
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t("CVC")}</label>
                      <input
                        className={inputClasses}
                        value={billing.cvc}
                        onChange={(e) =>
                          setBilling({ ...billing, cvc: e.target.value.replace(/\D/g, "").slice(0, 4) })
                        }
                        placeholder={t("123")}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* Order summary */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="order-1 lg:order-2 lg:sticky lg:top-24 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900">{t("Order Summary")}</h3>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPlanId(null);
                      setSearchParams({});
                    }}
                    className="text-sm font-semibold text-khamco-primary hover:underline"
                  >
                    {t("Change plan")}
                  </button>
                </div>

                <div className="flex justify-center mb-6">
                  <BillingCycleToggle value={billingCycle} onChange={handleBillingCycleChange} />
                </div>

                <div className="flex items-start justify-between pb-4 mb-4 border-b border-gray-100">
                  <div>
                    <p className="font-bold text-gray-900">{plan.name} {t("Plan")}</p>
                    <p className="text-sm text-gray-500">{plan.tagline}</p>
                  </div>
                  <p className="font-bold text-gray-900 whitespace-nowrap ml-4">
                    ${total}/{billingCycle}
                  </p>
                </div>

                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between text-lg font-extrabold text-gray-900 pt-4 border-t border-gray-100 mb-6">
                  <span>{t("Total due today")}</span>
                  <span>${total}</span>
                </div>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={checkoutMutation.isPending || (isAuthenticated && userDetailsQuery.isLoading)}
                  className="w-full bg-khamco-primary hover:bg-pink-600 disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold text-lg transition-colors shadow-lg flex items-center justify-center"
                >
                  {checkoutMutation.isPending ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t("Processing...")}
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      {t("Pay")} ${total}/{billingCycle}
                    </>
                  )}
                </motion.button>

                <p className="text-xs text-gray-400 text-center mt-4">
                  {t("By completing this purchase you agree to our")}{" "}
                  <Link to="/terms" className="underline">
                    {t("Terms of Service")}
                  </Link>
                  .
                </p>
              </motion.div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}